import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoSrc from '@/assets/logos/radium-logo-text.svg';
import { Button } from '@/components/interactive/Button';
import { MobileDrawer } from './MobileDrawer';

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/orchestration', label: 'Orchestration' },
  { to: '/personalization', label: 'Personalization' },
  { to: '/personalization/customers', label: 'Customers' },
  { to: '/about', label: 'About' },
  { to: '/docs/introduction', label: 'Docs' },
];

function Logo() {
  return (
    <Link
      to="/"
      style={{ display: 'inline-flex', alignItems: 'center' }}
      aria-label="Radium home"
    >
      <img
        src={logoSrc}
        alt="Radium"
        style={{
          height: 28,
          width: 'auto',
          maxWidth: 'min(220px, 52vw)',
          objectFit: 'contain',
          filter: 'drop-shadow(0 0 14px var(--radium-glow))',
        }}
      />
    </Link>
  );
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isPathMatch = (route: string, currentPath: string) =>
    currentPath === route ||
    (route !== '/' && currentPath.startsWith(`${route}/`));

  const activeTo = navItems
    .map((it) => it.to)
    .filter((to) => isPathMatch(to, pathname))
    .sort((a, b) => b.length - a.length)[0];

  return (
    <>
      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          padding: scrolled ? '12px 0' : '20px 0',
          background: scrolled
            ? 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)'
            : 'transparent',
          backdropFilter: scrolled ? 'blur(22px) saturate(160%)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(22px) saturate(160%)' : 'none',
          borderBottom: scrolled
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid transparent',
          boxShadow: scrolled ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : 'none',
          transition:
            'padding 240ms var(--ease-out), background 240ms var(--ease-out), border-color 240ms var(--ease-out)',
        }}
      >
        <div
          className="container"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Logo />
          <nav
            aria-label="Main navigation"
            style={{ display: 'flex', alignItems: 'center', gap: 32 }}
            className="header-nav"
          >
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                active={activeTo === it.to}
                label={it.label}
              />
            ))}
            <Button href="/contact" variant="primary" size="md">
              Contact us
            </Button>
          </nav>
          <button
            type="button"
            className="header-burger"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            style={{ display: 'none', padding: 8, color: 'var(--ink-primary)' }}
          >
            <svg
              aria-hidden="true"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </header>

      {drawerOpen && (
        <MobileDrawer onClose={() => setDrawerOpen(false)} items={navItems} />
      )}

      <style>{`
        @media (max-width: 920px) {
          .header-nav { gap: 22px !important; }
        }
        @media (max-width: 760px) {
          .header-nav { display: none !important; }
          .header-burger { display: inline-flex !important; }
        }
      `}</style>
    </>
  );
}

function NavLink({
  to,
  active,
  label,
}: {
  to: string;
  active: boolean;
  label: string;
}) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      style={{
        fontSize: 14,
        fontWeight: 500,
        color: active || hover ? 'var(--ink-primary)' : 'var(--ink-secondary)',
        transition: 'color 160ms var(--ease-out)',
        position: 'relative',
        padding: '4px 0',
        textDecoration: 'none',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {label}
      {active && (
        <span
          style={{
            position: 'absolute',
            bottom: -2,
            left: 0,
            right: 0,
            height: 1,
            background: 'var(--accent)',
            boxShadow: '0 0 8px var(--accent-glow)',
          }}
        />
      )}
    </Link>
  );
}

export { Logo };
